import { ReactElement, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SubRoutes } from '../routes/SubRoutes';
import { useUser } from '../hooks/useUser';

export default function MenuBar(props: { setMobileSideBar?: any; openFeedback?: () => void }): ReactElement {
  const { setMobileSideBar, openFeedback } = props;
  const [userProfile] = useUser();
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});

  const routes = SubRoutes()
    .filter((route: any) => !!route.icon)
    .filter((route: any) => {
      if (route.userFilter) {
        return route.userFilter(userProfile);
      }
      return true;
    });
  const { pathname } = useLocation();

  const basePathName = useMemo(() => {
    const path = pathname.split('/');
    return path.slice(0, 3).join('/');
  }, [pathname]);

  const toggleSubmenu = (routePath: string) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [routePath]: !prev[routePath],
    }));
  };

  const isSubmenuActive = (submenuItems: any[]) => {
    return submenuItems.some((item) => basePathName === item.path);
  };

  return (
    <div className="font-label cr-h-full cr-p-0">
      <ul className="cr-h-full cr-list-none cr-divide-y-2 cr-divide-solid cr-divide-gray-100 cr-bg-cirrusBlue-100 cr-shadow-xl">
        {routes.map((route: any) => {
          const hasSubmenu = route.submenu && route.submenu.length > 0;
          const isCurrentPath = hasSubmenu ? isSubmenuActive(route.submenu) : basePathName === route.path;
          // Default open when one of the children is the current page —
          // otherwise landing on a sub-page highlights the parent but leaves
          // the list shut, so there is nothing showing where you are. `??`
          // means an explicit toggle still wins.
          const isExpanded = expandedMenus[route.path] ?? isCurrentPath;
          const selected = isCurrentPath
            ? 'cr-bg-cirrusBlue-75 cr-border-solid cr-border-r-cirrusBlue-400 cr-border-r-4'
            : 'cr-border-r-0';

          return (
            <li key={route.path} className={'cr-text-base cr-font-bold cr-capitalize'}>
              {hasSubmenu ? (
                <>
                  <button
                    className={
                      `cr-w-full cr-transition-all cr-duration-300 ${selected} hover:cr-bg-cirrusBlue-75 ` +
                      'cr-flex cr-cursor-pointer cr-flex-row cr-justify-start cr-border-0 cr-bg-cirrusBlue-100 cr-pb-2 cr-pl-8 cr-pr-6 cr-pt-4 cr-text-left cr-text-base cr-font-bold cr-capitalize cr-text-cirrusBlue-400'
                    }
                    onClick={() => toggleSubmenu(route.path)}
                  >
                    <div className="cr-flex-1 cr-text-left">{route.label}</div>
                    <svg
                      className={`cr-ml-2 cr-h-4 cr-w-4 cr-transition-transform cr-duration-200 ${isExpanded ? 'cr-rotate-90' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  {isExpanded && (
                    <ul className="cr-m-0 cr-list-none cr-divide-y cr-divide-solid cr-divide-cirrusBlue-75 cr-bg-cirrusBlue-50 cr-p-0">
                      {route.submenu
                        .filter((submenuItem: any) => {
                          if (submenuItem.userFilter) {
                            return submenuItem.userFilter(userProfile);
                          }
                          return true;
                        })
                        .map((submenuItem: any) => {
                          const isSubmenuItemActive = basePathName === submenuItem.path;
                          const submenuSelected = isSubmenuItemActive
                            ? 'cr-bg-cirrusBlue-75 cr-border-solid cr-border-r-cirrusBlue-400 cr-border-r-4'
                            : 'cr-border-r-0';

                          return (
                            <li key={submenuItem.path} className="cr-text-base cr-font-bold cr-capitalize">
                              <Link
                                className={
                                  `cr-transition-all cr-duration-300 ${submenuSelected} hover:cr-bg-cirrusBlue-75 ` +
                                  'cr-flex cr-flex-row cr-justify-start cr-pb-2 cr-pl-12 cr-pr-6 cr-pt-3 cr-text-sm cr-text-blue-800 cr-no-underline'
                                }
                                to={submenuItem.path}
                                onClick={() => setMobileSideBar && setMobileSideBar(false)}
                              >
                                <div className="cr-text-left">{submenuItem.label}</div>
                              </Link>
                            </li>
                          );
                        })}
                    </ul>
                  )}
                </>
              ) : (
                <Link
                  className={
                    `cr-transition-all cr-duration-300 ${selected} hover:cr-bg-cirrusBlue-75 ` +
                    'cr-flex cr-flex-row cr-justify-start cr-pb-2 cr-pl-8 cr-pr-6 cr-pt-4 cr-text-cirrusBlue-400 cr-no-underline'
                  }
                  to={route.path}
                  onClick={() => setMobileSideBar && setMobileSideBar(false)}
                >
                  <div className="cr-text-left">{route.label}</div>
                </Link>
              )}
            </li>
          );
        })}

        {/* Not a route — it opens the report form in place, so the user keeps the
            page they are reporting on and the form can capture its context. */}
        {openFeedback && (
          <li className="cr-text-base cr-font-bold cr-capitalize">
            <button
              type="button"
              className={
                'cr-w-full cr-cursor-pointer cr-border-0 cr-bg-cirrusBlue-100 cr-transition-all cr-duration-300 ' +
                'hover:cr-bg-cirrusBlue-75 cr-flex cr-flex-row cr-justify-start ' +
                'cr-pb-2 cr-pl-8 cr-pr-6 cr-pt-4 cr-text-left cr-text-base cr-font-bold cr-capitalize ' +
                'cr-text-cirrusBlue-400'
              }
              onClick={() => {
                if (setMobileSideBar) setMobileSideBar(false);
                openFeedback();
              }}
            >
              <div className="cr-text-left">Feedback</div>
            </button>
          </li>
        )}
      </ul>
    </div>
  );
}
